import * as i0 from '@angular/core';
import { inject, DestroyRef, ChangeDetectorRef, Component, ViewEncapsulation, ChangeDetectionStrategy, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgControl } from '@angular/forms';
import * as i2 from '@taiga-ui/cdk/directives/native-validator';
import { TuiNativeValidator } from '@taiga-ui/cdk/directives/native-validator';
import { tuiWatch } from '@taiga-ui/cdk/observables';
import { tuiInjectElement } from '@taiga-ui/cdk/utils/dom';
import { tuiCreateToken, tuiProvideOptions, tuiIsString } from '@taiga-ui/cdk/utils/miscellaneous';
import * as i1 from '@taiga-ui/core/directives/appearance';
import { TuiAppearance } from '@taiga-ui/core/directives/appearance';
import { tuiInjectIconResolver } from '@taiga-ui/core/tokens';
const _c0 = ["type", "checkbox", "tuiSwitch", ""];
const TUI_SWITCH_DEFAULT_OPTIONS = {
  showIcons: true,
  size: 'm',
  icon: '@tui.check',
  appearance: el => el.checked ? 'primary' : 'secondary'
};
const TUI_SWITCH_OPTIONS = tuiCreateToken(TUI_SWITCH_DEFAULT_OPTIONS);
function tuiSwitchOptionsProvider(options) {
  return tuiProvideOptions(TUI_SWITCH_OPTIONS, options, TUI_SWITCH_DEFAULT_OPTIONS);
}
let TuiSwitch = /*#__PURE__*/(() => {
  class TuiSwitch {
    constructor() {
      this.appearance = inject(TuiAppearance);
      this.resolver = tuiInjectIconResolver();
      this.destroyRef = inject(DestroyRef);
      this.cdr = inject(ChangeDetectorRef);
      this.options = inject(TUI_SWITCH_OPTIONS);
      this.el = tuiInjectElement();
      this.control = inject(NgControl, {
        self: true,
        optional: true
      });
      this.size = this.options.size;
      this.showIcons = this.options.showIcons;
    }
    ngOnInit() {
      this.control?.valueChanges?.pipe(tuiWatch(this.cdr), takeUntilDestroyed(this.destroyRef)).subscribe();
    }
    ngDoCheck() {
      this.appearance.tuiAppearance = this.options.appearance(this.el);
    }
    get icon() {
      const {
        options,
        resolver,
        size
      } = this;
      const icon = tuiIsString(options.icon) ? options.icon : options.icon(size);
      return this.showIcons && icon ? `url(${resolver(icon)})` : null;
    }
    static {
      this.ɵfac = function TuiSwitch_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiSwitch)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: TuiSwitch,
        selectors: [["input", "type", "checkbox", "tuiSwitch", ""]],
        hostAttrs: ["role", "switch"],
        hostVars: 6,
        hostBindings: function TuiSwitch_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵhostProperty("disabled", !ctx.control || ctx.control.disabled);
            i0.ɵɵattribute("data-size", ctx.size);
            i0.ɵɵstyleProp("--t-checked-icon", ctx.icon);
            i0.ɵɵclassProp("_readonly", !ctx.control);
          }
        },
        inputs: {
          size: "size",
          showIcons: "showIcons"
        },
        features: [i0.ɵɵHostDirectivesFeature([{
          directive: i1.TuiAppearance,
          inputs: ["tuiAppearanceState", "tuiAppearanceState", "tuiAppearanceFocus", "tuiAppearanceFocus", "tuiAppearanceMode", "tuiAppearanceMode"]
        }, i2.TuiNativeValidator])],
        attrs: _c0,
        decls: 0,
        vars: 0,
        template: function TuiSwitch_Template(rf, ctx) {},
        styles: ["[tuiSwitch]{transition-property:background,box-shadow;transition-duration:var(--tui-duration, .3s);transition-timing-function:ease-in-out;inline-size:3rem;block-size:1.5rem;border-radius:2rem;overflow:hidden;cursor:pointer;margin:0;flex-shrink:0;color:#fff!important}[tuiSwitch][data-size=s]{block-size:1rem;inline-size:2rem}[tuiSwitch][data-size=s]:before{inline-size:1rem;transform:translate(-1rem);-webkit-mask-size:.75rem;mask-size:.75rem}[tuiSwitch][data-size=s]:after{inline-size:1rem;box-shadow:-2.625rem 0 0 .5rem var(--tui-background-base);outline-width:.167rem;transform:scale(.375)}[tuiSwitch][data-size=s]:checked:after{transform:scale(.375) translate(2.625rem)}[tuiSwitch]:checked:before{transform:none}[tuiSwitch]:checked:after{transform:scale(.33333) translate(4.5rem)}[tuiSwitch]:disabled._readonly{opacity:1}[tuiSwitch]:before,[tuiSwitch]:after{transition-property:transform;transition-duration:var(--tui-duration, .3s);transition-timing-function:ease-in-out;content:\"\";position:absolute;block-size:100%;inline-size:1.5rem}[tuiSwitch]:before{display:var(--t-checked-icon, none);background:currentColor;-webkit-mask:var(--t-checked-icon) no-repeat center;mask:var(--t-checked-icon) no-repeat center;-webkit-mask-size:1rem;mask-size:1rem;transform:translate(-1.5rem)}[tuiSwitch]:after{right:0;border-radius:100%;transform:scale(.33333);box-shadow:-4.5rem 0 0 .75rem var(--tui-background-base);outline:.375rem solid var(--tui-background-neutral-2-pressed);outline-offset:var(--t-checked-icon, 20rem)}[tuiSwitch]:invalid:not([data-mode]),[tuiSwitch][data-mode~=invalid]{color:#fff}\n"],
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return TuiSwitch;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { TUI_SWITCH_DEFAULT_OPTIONS, TUI_SWITCH_OPTIONS, TuiSwitch, tuiSwitchOptionsProvider };
